import React, { useEffect, useMemo, useRef } from 'react';

import TabelaPaginada, { ColunaTabela } from './TabelaPaginada';
import type { AtendimentoResumo } from '../../api/dashboardOficinaApi';
import '../../components/modal/add-car/AddCarModal.css';
import './modal-close-button.css'; // X padrão do sistema

/**
 * Detalhamento por placa (at-86): quais atendimentos compõem um número do dashboard.
 *
 * Existe porque número agregado sem caminho até o caso concreto gera desconfiança — a primeira
 * pergunta de quem olha um gráfico de gestão é "quais são?". Sem essa saída, o gestor recorre a
 * exportar CSV e conferir à mão, que é onde o painel perde a função.
 *
 * ⚠️ Fecha por clique fora e por Escape, e recebe `sidebarRef` porque o fechamento exige clique
 * fora do modal E fora da sidebar — sem a ref, o caminho fica inerte.
 */

export type AtendimentoDetalhe = AtendimentoResumo;

interface Props {
  titulo: string;
  /** Placas que compõem o número clicado. */
  placas: string[];
  /** Lista completa dos atendimentos do recorte, para resolver os detalhes de cada placa. */
  atendimentos: AtendimentoDetalhe[];
  /**
   * Colunas DEPOIS de Placa e Modelo — a informação do recorte que o usuário abriu.
   *
   * ⚠️ São obrigatórias por desenho: um modal que mostra sempre as mesmas colunas genéricas não
   * explica o número que o abriu. Quem clica em "Veículos por categoria" quer ver a categoria na
   * linha; quem clica em "Valor médio por veículo" precisa do valor de CADA um, senão a média
   * fica sem verificação possível.
   */
  colunas: Array<ColunaTabela<AtendimentoDetalhe>>;
  onFechar: () => void;
  sidebarRef?: React.RefObject<HTMLElement>;
}

const DetalhePlacasModal: React.FC<Props> = ({
  titulo,
  placas,
  atendimentos,
  colunas,
  onFechar,
  sidebarRef,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const aoClicarFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      if (ref.current && !ref.current.contains(alvo) && !sidebarRef?.current?.contains(alvo)) {
        onFechar();
      }
    };
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onFechar();
    };
    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [onFechar, sidebarRef]);

  const linhas = useMemo(() => {
    const porPlaca = new Map(atendimentos.map((a) => [a.plate, a]));
    return placas
      .map(
        (placa) =>
          porPlaca.get(placa) ?? {
            plate: placa,
            model: null,
            carClass: 'Não informada',
            esteira: 'Não informada',
            naOficina: false,
            diasNaOficina: 0,
            noSga: false,
            categoria: null,
            marca: null,
            anoModelo: null,
            idadeAnos: null,
            valorProtegido: null,
            consultor: null,
            cooperativa: null,
            bairro: null,
            cidade: null,
            faixaEtaria: null,
            tempoDeAssociacao: null,
            situacaoVeiculo: null,
            situacaoAssociado: null,
            damageType: null,
            retornos: 0,
            prazoRestante: null,
            horasTrabalhadas: 0,
            diasNaEsteiraAtual: 0,
          },
      )
      .sort((a, b) => a.plate.localeCompare(b.plate));
  }, [placas, atendimentos]);

  return (
    <div className="modal-overlay">
      <div className="do-modal" ref={ref} role="dialog" aria-modal="true" aria-label={titulo}>
        <div className="do-modal-header">
          <h2 className="do-modal-titulo" title={titulo}>
            {titulo}
          </h2>
          <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="do-modal-corpo">
          <p className="do-modal-apoio">
            {linhas.length} {linhas.length === 1 ? 'atendimento compõe' : 'atendimentos compõem'}{' '}
            este número.
          </p>

          <TabelaPaginada<AtendimentoDetalhe>
            itens={linhas}
            chave={(item) => item.plate}
            vazio="Nenhum atendimento neste número."
            porPagina={10}
            colunas={[
              { titulo: 'Placa', largura: 110, texto: (i) => i.plate },
              { titulo: 'Modelo', largura: 220, texto: (i) => i.model ?? '—' },
              ...colunas,
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default DetalhePlacasModal;
